import React, { useState, useEffect } from 'react';
import Carousel from 'react-material-ui-carousel';
import { Container, Typography, Box, CircularProgress, Fade, Grid } from '@mui/material';
import { SectionContainer, PageTitle, LoadingContainer, FeatureCard } from '../Components/StyledComponents';
import { loadGallery } from '../api/gallery';
import ChairIcon from '@mui/icons-material/Chair';
import WeekendIcon from '@mui/icons-material/Weekend';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import KingBedIcon from '@mui/icons-material/KingBed';
import BuildIcon from '@mui/icons-material/Build';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Seo from "../Components/Seo";

const Services = () => {
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const services = [
    {
      icon: <WeekendIcon sx={{ fontSize: 50, color: 'secondary.main', mb: 2 }} />,
      title: "Banken Stofferen",
      description: "Geef uw bank nieuw leven met professionele stoffering. Van moderne designs tot klassieke stijlen."
    },
    {
      icon: <ChairIcon sx={{ fontSize: 50, color: 'secondary.main', mb: 2 }} />,
      title: "Stoelen Bekleden",
      description: "Eetkamerstoelen, fauteuils en barkrukken vakkundig opnieuw gestoffeerd naar uw wens."
    },
    {
      icon: <KingBedIcon sx={{ fontSize: 50, color: 'secondary.main', mb: 2 }} />,
      title: "Hoofdeinden",
      description: "Maatwerk hoofdeinden voor uw bed in elke gewenste stof en stijl voor een luxe uitstraling."
    },
    {
      icon: <EventSeatIcon sx={{ fontSize: 50, color: 'secondary.main', mb: 2 }} />,
      title: "Eetkamerbanken",
      description: "Op maat gemaakte eetkamerbanken die perfect passen bij uw interieur en eettafel."
    },
    {
      icon: <BuildIcon sx={{ fontSize: 50, color: 'secondary.main', mb: 2 }} />,
      title: "Restauratie",
      description: "Restauratie van antieke meubels met respect voor het originele karakter en vakmanschap."
    },
    {
      icon: <AutoFixHighIcon sx={{ fontSize: 50, color: 'secondary.main', mb: 2 }} />,
      title: "Maatwerk",
      description: "Volledig op maat gemaakte stoffeerprojecten volgens uw specifieke wensen en afmetingen."
    },
  ];

  useEffect(() => {
    const fetchDienstenImages = async () => {
      try {
        setLoading(true);
        const gallery = await loadGallery();
        setImageUrls(gallery.servicesGallery);
        setError(null);
      } catch (error) {
        console.error('Error fetching diensten images:', error);
        setError('Kon afbeeldingen niet laden. Controleer de Azure instellingen en probeer het later opnieuw.');
      } finally {
        setLoading(false);
      }
    };

    fetchDienstenImages();
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Laden van diensten...
        </Typography>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <LoadingContainer>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </LoadingContainer>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <Box>
        <Seo
          title="Diensten | Stofferen, Restauratie en Maatwerk | Meester Stoffeerders"
          description="Ontdek onze stoffeerdiensten in Almere: banken stofferen, stoelen bekleden, hoofdeinden, restauratie en maatwerk meubelstoffering."
          path="/services"
        />
        {/* Hero Section */}
        <SectionContainer sx={{ pt: 8, pb: 6 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <PageTitle>Onze Diensten</PageTitle>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ mt: 3, maxWidth: '800px', mx: 'auto' }}
              >
                Van klassieke restauratie tot modern maatwerk - wij bieden een compleet pakket aan professionele stoffeerdiensten
              </Typography>
            </Box>

            {/* Services Grid */}
            <Grid container spacing={3} sx={{ mb: 8 }}>
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <FeatureCard>
                    {service.icon}
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {service.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>

            {/* What We Offer Section */}
            <Box sx={{ 
              backgroundColor: 'background.paper', 
              borderRadius: 3, 
              p: 4, 
              mb: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
                Wat Wij Bieden
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Materialen & Stofkeuze
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    • Uitgebreid assortiment hoogwaardige meubelstof<br/>
                    • Leder en kunstleder in vele kleuren<br/>
                    • Advies op maat voor de juiste stoffenkeuze<br/>
                    • Stalen beschikbaar voor thuisbezorging
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Werkwijze
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    • Gratis adviesgesprek en offerte<br/>
                    • Ophalen en bezorgen mogelijk<br/>
                    • Vakkundige uitvoering met oog voor detail<br/>
                    • Garantie op al ons werk
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Gallery Section */}
            {loading ? (
              <LoadingContainer>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" color="text.secondary">
                  Laden van voorbeelden...
                </Typography>
              </LoadingContainer>
            ) : error ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="error">
                  {error}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
                  Voorbeelden van Ons Werk
                </Typography>
                {imageUrls.length > 0 ? (
                  <Box 
                    sx={{ 
                      maxWidth: '1000px', 
                      mx: 'auto',
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    }}
                  >
                    <Carousel
                      animation="slide"
                      duration={500}
                      navButtonsAlwaysVisible
                      indicators={true}
                      autoPlay={false}
                      navButtonsProps={{
                        style: {
                          backgroundColor: 'rgba(44, 62, 80, 0.8)',
                          color: 'white',
                          borderRadius: '50%',
                          padding: '10px',
                        }
                      }}
                      indicatorIconButtonProps={{
                        style: {
                          color: 'rgba(44, 62, 80, 0.3)',
                          margin: '0 5px',
                        }
                      }}
                      activeIndicatorIconButtonProps={{
                        style: {
                          color: '#E67E22',
                        }
                      }}
                    >
                      {imageUrls.map((image, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: 'relative',
                            minHeight: { xs: '280px', md: '520px' },
                            maxHeight: '68vh',
                            backgroundColor: 'background.paper',
                          }}
                        >
                          <img
                            src={image.src}
                            alt={image.alt || `voorbeeld-${index + 1}`}
                            onError={(event) => {
                              event.currentTarget.style.display = 'none';
                            }}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              objectPosition: 'center center',
                              backgroundColor: 'rgba(255,255,255,0.82)',
                            }}
                            loading="lazy"
                          />
                        </Box>
                      ))}
                    </Carousel>
                  </Box>
                ) : (
                  <Typography variant="h6" align="center" color="text.secondary">
                    Geen afbeeldingen beschikbaar
                  </Typography>
                )}
              </>
            )}
          </Container>
        </SectionContainer>
      </Box>
    </Fade>
  );
};

export default Services;
